"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { env } from "@/env";
import { authClient } from "@/lib/auth-client-instance";

const ERROR_MESSAGES: Record<string, string> = {
  account_not_linked:
    "An account with this email already exists. Please sign in with your original method.",
  account_already_linked_to_different_user:
    "This social account is already linked to a different user.",
  signup_disabled: "Sign up is currently disabled.",
  unable_to_create_user: "Unable to create your account. Please try again.",
  unable_to_create_session: "Unable to create a session. Please try again.",
  unable_to_get_user_info:
    "Unable to retrieve your information from the provider.",
  unable_to_link_account: "Unable to link your account. Please try again.",
  email_not_found: "No account found with this email address.",
  oauth_provider_not_found: "The authentication provider was not found.",
  state_mismatch: "Authentication failed. Please try again.",
  invalid_code: "Authentication failed. Please try again.",
  internal_server_error: "Something went wrong. Please try again.",
};

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin text-muted-foreground"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export function ThemedLogin({
  tenantName,
  tenantLogoUrl,
}: {
  tenantName?: string;
  tenantLogoUrl?: string;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(() => {
    const errorCode = searchParams.get("error");
    if (!errorCode) return null;
    return ERROR_MESSAGES[errorCode] ?? "An error occurred. Please try again.";
  });
  const [submitting, setSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);

  const isLoading = submitting || oauthLoading !== null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const { error: signInError } = await authClient.signIn.email({
        email,
        password,
      });
      if (signInError) {
        setError(signInError.message ?? "Sign in failed");
        setSubmitting(false);
        return;
      }
      router.push("/select-org");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sign in failed");
      setSubmitting(false);
    }
  };

  const handleOAuth = async (provider: "google" | "microsoft") => {
    setOauthLoading(provider);
    setError(null);
    try {
      await authClient.signIn.social({
        provider,
        callbackURL: "/select-org",
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "OAuth sign in failed");
      setOauthLoading(null);
    }
  };

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-background">
      <div className="w-full max-w-[400px] overflow-hidden rounded-2xl border border-border bg-secondary shadow-2xl">
        {/* Logo Section */}
        <div className="flex flex-col items-center pb-2 pt-8">
          {tenantLogoUrl ? (
            // oxlint-disable-next-line nextjs/no-img-element
            <img
              src={tenantLogoUrl}
              alt={`${tenantName ?? "App"} Logo`}
              className="mb-3 h-14 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <>
              <Image
                src="/logo.svg"
                alt="Logo"
                width={56}
                height={56}
                className="mb-3"
              />
              <span className="text-xs font-semibold tracking-[0.25em] text-muted-foreground">
                {env.NEXT_PUBLIC_APP_NAME}
              </span>
            </>
          )}
        </div>

        {/* Header */}
        <div className="px-8 pb-1 pt-4 text-center">
          <h1 className="text-xl font-bold text-foreground">
            Sign in to {tenantName ?? env.NEXT_PUBLIC_APP_NAME}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Welcome back! Please sign in to continue
          </p>
        </div>

        {/* Content */}
        <div className="space-y-5 px-8 pb-8 pt-5">
          {/* Error from OAuth redirect or form */}
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-center text-sm text-destructive"
            >
              {error}
            </div>
          )}

          {/* Social Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              disabled={isLoading}
              onClick={() => handleOAuth("google")}
              className="flex h-[38px] flex-1 cursor-pointer items-center justify-center rounded-lg border border-border bg-background transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {oauthLoading === "google" ? <Spinner /> : <GoogleIcon />}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={() => handleOAuth("microsoft")}
              className="flex h-[38px] flex-1 cursor-pointer items-center justify-center rounded-lg border border-border bg-background transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {oauthLoading === "microsoft" ? <Spinner /> : <MicrosoftIcon />}
            </button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-secondary px-3 text-muted-foreground">
                or
              </span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label
                htmlFor="email"
                className="text-sm font-semibold text-foreground"
              >
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="h-10 rounded-lg border-border bg-background"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="password"
                className="text-sm font-semibold text-foreground"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="h-10 rounded-lg border-border bg-background"
              />
            </div>

            <Button
              type="submit"
              className="h-10 w-full rounded-lg text-sm font-semibold"
              disabled={isLoading}
            >
              {submitting ? "Signing in..." : "Continue"}
              {!submitting && (
                <svg
                  className="ml-1.5 h-3 w-3"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </Button>
          </form>

          <div className="text-center text-sm">
            <Link
              href="/forgot-password"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              Forgot your password?
            </Link>
          </div>
        </div>
      </div>
      {/* Plain <a> so this link always works, even mid-redirect or during loading */}
      {/* oxlint-disable-next-line nextjs/no-html-link-for-pages */}
      <a
        href="/clear-session"
        className="mt-3 text-center text-xs text-muted-foreground/70 transition-colors hover:text-muted-foreground"
      >
        Having trouble signing in?
      </a>
    </div>
  );
}
